import { ChangeDetectionStrategy, Component, ElementRef, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractComponent,
  AttendanceDashboardOverview,
  AttendanceLiveSnapshot,
  DashboardCrmKpis,
  DashboardCrmService,
  DashboardService,
  DateUtil,
  GoalStatus,
} from 'lib-trend-core';
import { forkJoin, takeUntil } from 'rxjs';

/**
 * The cadences the panel offers. One minute is the floor on purpose: the readings behind the
 * panel are a full period aggregation, and asking for them faster than that would cost the
 * company more than a wall screen can show.
 */
const REFRESH_OPTIONS_MINUTES = [1, 5, 10, 30, 60];

/** What the panel refreshes at until somebody chooses otherwise. */
const DEFAULT_REFRESH_MINUTES = 5;

/** Where the chosen cadence is kept, so a reloaded wall panel comes back on it. */
const REFRESH_STORAGE_KEY = 'dashboard:tv:refreshMinutes';

/** How often the countdown bar redraws while the panel is live. */
const TICK_MS = 1000;

/**
 * Painel TV — the attendance operation on a wall screen.
 *
 * Three things make it different from the dashboard tabs beside it:
 *
 * 1. **It refreshes itself on a cadence chosen in its header** (1 to 60 minutes, five by
 *    default), and ONLY while its tab is the one on screen and the browser tab is visible. A
 *    panel polling in the background would keep a company's dashboard hitting the API all day
 *    for a screen nobody is looking at.
 * 2. **It compares each reading with the previous one.** The deltas (▼ -2, ▲ +3 min) come from
 *    keeping the last payload in memory, not from the API: the panel is the only thing that
 *    knows what it showed five minutes ago.
 * 3. **It goes fullscreen**, through the browser's own Fullscreen API on the panel element.
 */
@Component({
  selector: 'app-dashboard-tv',
  templateUrl: './dashboard-tv.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DashboardTvComponent extends AbstractComponent implements OnInit, OnDestroy {

  @ViewChild('panel', { static: false }) panel: ElementRef<HTMLElement>;

  live: AttendanceLiveSnapshot | null = null;
  overview: AttendanceDashboardOverview | null = null;
  crmKpis: DashboardCrmKpis | null = null;
  sellers: Array<Record<string, string | number>> = [];
  products: Array<Record<string, string | number>> = [];
  openByStage: Array<Record<string, string | number>> = [];

  /** The reading before the current one — what every delta on screen is measured against. */
  private previousLive: AttendanceLiveSnapshot | null = null;
  private previousOverview: AttendanceDashboardOverview | null = null;

  updatedAt: Date | null = null;
  /** A read is in flight — keeps the manual button from stacking requests. */
  reading = false;
  isFullscreen = false;

  readonly refreshOptions = REFRESH_OPTIONS_MINUTES;

  /** The chosen cadence, in minutes. */
  refreshMinutes = this.restoreRefreshMinutes();

  /** Seconds until the next read; drives the thin bar under the header. */
  secondsToRefresh = this.refreshMinutes * 60;

  get refreshSeconds(): number {
    return this.refreshMinutes * 60;
  }

  /** True while this tab is the one on screen. Nothing is fetched otherwise. */
  private tabVisible = false;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private tickTimer: ReturnType<typeof setInterval> | null = null;

  private readonly onTabShown = (event: Event) => {
    const target = (event.target as HTMLElement)?.getAttribute?.('data-bs-target');
    this.tabVisible = target === '#tv';
    this.syncPolling();
  };

  private readonly onVisibilityChange = () => this.syncPolling();

  private readonly onFullscreenChange = () => {
    this.isFullscreen = !!document.fullscreenElement;
  };

  constructor(
    injector: Injector,
    private readonly dashboardService: DashboardService,
    private readonly dashboardCrmService: DashboardCrmService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    // Bootstrap dispatches `shown.bs.tab` on the trigger and it bubbles, so the panel can tell
    // on its own when it is the tab on screen — no wiring from the dashboard around it.
    document.addEventListener('shown.bs.tab', this.onTabShown);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    document.removeEventListener('shown.bs.tab', this.onTabShown);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    this.stopPolling();
  }

  // ---------------------------------------------------------------------------
  // Polling
  // ---------------------------------------------------------------------------

  /**
   * Start or stop the refresh cycle. Running means BOTH that this tab is the one selected and
   * that the browser tab is visible: a panel on a monitor nobody is looking at should not keep
   * the API busy.
   */
  private syncPolling(): void {
    const shouldRun = this.tabVisible && !document.hidden;

    if (shouldRun && !this.refreshTimer) {
      this.load();
      this.refreshTimer = setInterval(() => this.load(), this.refreshMinutes * 60 * 1000);
      this.tickTimer = setInterval(() => this.tick(), TICK_MS);
      return;
    }

    if (!shouldRun && this.refreshTimer) {
      this.stopPolling();
    }
  }

  private stopPolling(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  private tick(): void {
    this.secondsToRefresh = Math.max(0, this.secondsToRefresh - 1);
  }

  /** How much of the cycle has elapsed, for the bar under the header. */
  get refreshProgress(): number {
    return ((this.refreshSeconds - this.secondsToRefresh) / this.refreshSeconds) * 100;
  }

  /** Read everything again, by hand — the button beside the clock. */
  refreshNow(): void {
    this.load();
  }

  /**
   * Change the cadence. The running cycle is restarted rather than left to finish, so the
   * countdown on screen always matches the interval the panel is actually on.
   */
  chooseRefreshMinutes(minutes: number): void {
    if (minutes === this.refreshMinutes) {
      return;
    }

    this.refreshMinutes = minutes;
    this.secondsToRefresh = this.refreshSeconds;

    try {
      localStorage.setItem(REFRESH_STORAGE_KEY, String(minutes));
    } catch {
      // A panel in a locked-down browser simply forgets the choice on reload.
    }

    if (this.refreshTimer) {
      this.stopPolling();
      this.syncPolling();
    }
  }

  /** The stored cadence, when it is still one of the options on offer. */
  private restoreRefreshMinutes(): number {
    try {
      const stored = Number(localStorage.getItem(REFRESH_STORAGE_KEY));
      return REFRESH_OPTIONS_MINUTES.includes(stored) ? stored : DEFAULT_REFRESH_MINUTES;
    } catch {
      return DEFAULT_REFRESH_MINUTES;
    }
  }

  private load(): void {
    if (this.reading) {
      return;
    }
    this.reading = true;
    this.secondsToRefresh = this.refreshSeconds;

    const today = DateUtil.toCalendarDay(new Date());
    const month = this.currentMonthRange();

    forkJoin({
      live: this.dashboardService.getAttendancesLive(null, null, null),
      overview: this.dashboardService.getAttendancesOverview(today, today, null, null, null),
      crm: this.dashboardCrmService.getDashboardCrmKpis(month),
      sellers: this.dashboardCrmService.getDashboardUser(month),
      products: this.dashboardCrmService.getDashboardProducts(month),
      stages: this.dashboardCrmService.getOpenByStage({}),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          // Keep what was on screen before overwriting it: the deltas are the difference between
          // this reading and the last one, and nothing else remembers it.
          this.previousLive = this.live;
          this.previousOverview = this.overview;

          this.live = result.live;
          this.overview = result.overview;
          this.crmKpis = result.crm?.current ?? null;
          this.sellers = result.sellers ?? [];
          this.products = result.products ?? [];
          this.openByStage = result.stages ?? [];
          this.updatedAt = new Date();
        },
        complete: () => (this.reading = false),
        error: () => (this.reading = false),
      });
  }

  /** The calendar month being read, as the CRM endpoints take it. */
  private currentMonthRange(): Record<string, string> {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { dateStart: DateUtil.toCalendarDay(first), dateEnd: DateUtil.toCalendarDay(last) };
  }

  // ---------------------------------------------------------------------------
  // Fullscreen
  // ---------------------------------------------------------------------------

  toggleFullscreen(): void {
    const element = this.panel?.nativeElement;
    if (!element) {
      return;
    }

    if (document.fullscreenElement) {
      document.exitFullscreen?.();
      return;
    }
    element.requestFullscreen?.().catch(() => {
      // A browser may refuse fullscreen when the click did not reach it as a user gesture.
      // Nothing to recover: the panel keeps working in the page.
    });
  }

  // ---------------------------------------------------------------------------
  // Deltas against the previous reading
  // ---------------------------------------------------------------------------

  /** `null` on the first reading: there is nothing to compare against yet. */
  delta(current: number | null | undefined, previous: number | null | undefined): number | null {
    if (current === null || current === undefined || previous === null || previous === undefined) {
      return null;
    }
    const difference = Number(current) - Number(previous);
    return Number.isFinite(difference) && difference !== 0 ? difference : null;
  }

  liveDelta(field: keyof AttendanceLiveSnapshot): number | null {
    return this.delta(this.live?.[field] as number, this.previousLive?.[field] as number);
  }

  /** A count: "+3" / "-2". */
  formatDelta(value: number | null): string {
    if (value === null) {
      return '';
    }
    return `${value > 0 ? '+' : '−'}${Math.abs(value)}`;
  }

  /** A duration, in whole minutes — the unit a wall screen can read from across the room. */
  formatDeltaMinutes(value: number | null): string {
    if (value === null) {
      return '';
    }
    const minutes = Math.round(Math.abs(value));
    return `${value > 0 ? '+' : '−'}${minutes} min`;
  }

  /** A rate: percentage points, not percent of a percent. */
  formatDeltaPoints(value: number | null): string {
    if (value === null) {
      return '';
    }
    return `${value > 0 ? '+' : '−'}${Math.abs(value).toFixed(1).replace('.', ',')} pp`;
  }

  /** Green when the figure moved the way the operation wants it to. */
  deltaClass(value: number | null, higherIsBetter: boolean = true): string {
    if (value === null) {
      return 'text-muted';
    }
    const improved = value > 0 ? higherIsBetter : !higherIsBetter;
    return improved ? 'text-success' : 'text-danger';
  }

  deltaIcon(value: number | null): string {
    if (value === null) {
      return '';
    }
    return value > 0 ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold';
  }

  // ---------------------------------------------------------------------------
  // The month's sales target
  // ---------------------------------------------------------------------------

  get salesTarget(): number {
    return Number(this.overview?.goals?.monthlySalesTarget ?? 0);
  }

  get salesSoFar(): number {
    return Number(this.crmKpis?.wonValue ?? 0);
  }

  get salesProgress(): number {
    return this.salesTarget > 0 ? Math.min((this.salesSoFar / this.salesTarget) * 100, 100) : 0;
  }

  get salesRemaining(): number {
    return Math.max(0, this.salesTarget - this.salesSoFar);
  }

  /**
   * Working days left in the month, this one included. Monday to Friday with no holiday
   * calendar — the same working day the CRM's cadence uses.
   */
  get workingDaysLeft(): number {
    const now = new Date();
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    let days = 0;
    for (let day = now.getDate(); day <= last; day++) {
      const weekDay = new Date(now.getFullYear(), now.getMonth(), day).getDay();
      if (weekDay !== 0 && weekDay !== 6) {
        days++;
      }
    }
    return days;
  }

  get perWorkingDay(): number {
    const days = this.workingDaysLeft;
    return days > 0 ? this.salesRemaining / days : this.salesRemaining;
  }

  // ---------------------------------------------------------------------------
  // Rankings
  // ---------------------------------------------------------------------------

  /** Sellers of the month, by won value, biggest first — only those who actually won something. */
  get topSellers(): Array<{ name: string; value: number; deals: number }> {
    return (this.sellers ?? [])
      .map((row) => ({
        name: String(row['userName'] ?? '').trim() || '—',
        value: Number(row['wonValue'] ?? 0),
        deals: Number(row['wonOpportunities'] ?? 0),
      }))
      .filter((row) => row.deals > 0 || row.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }

  get topProducts(): Array<{ name: string; value: number; sales: number }> {
    return (this.products ?? [])
      .map((row) => ({
        name: String(row['productName'] ?? row['name'] ?? '').trim() || '—',
        value: Number(row['totalValue'] ?? row['value'] ?? 0),
        sales: Number(row['totalSales'] ?? row['sales'] ?? row['quantity'] ?? 0),
      }))
      .filter((row) => row.sales > 0 || row.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 3);
  }

  /** Open deals per stage, in funnel order, with the share each one holds of the open value. */
  get funnelStages(): Array<{ name: string; deals: number; value: number; share: number }> {
    const rows = (this.openByStage ?? []).map((row) => ({
      name: String(row['stageName'] ?? '—'),
      deals: Number(row['deals'] ?? 0),
      value: Number(row['value'] ?? 0),
    }));
    const biggest = Math.max(0, ...rows.map((row) => row.value));
    return rows.map((row) => ({ ...row, share: biggest > 0 ? (row.value / biggest) * 100 : 0 }));
  }

  // ---------------------------------------------------------------------------
  // Formatting (the panel shares the dashboard's rules)
  // ---------------------------------------------------------------------------

  formatMinutes(minutes: number | null | undefined): string {
    if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) {
      return '—';
    }
    const totalSeconds = Math.max(0, Math.round(minutes * 60));
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return [hours, mins, secs].map((part) => String(part).padStart(2, '0')).join(':');
  }

  formatPercent(value: number | null | undefined, fractionDigits = 1): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return '—';
    }
    return `${value.toFixed(fractionDigits).replace('.', ',')}%`;
  }

  formatSigned(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return '—';
    }
    return `${value > 0 ? '+' : ''}${value.toFixed(0)}%`;
  }

  formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return '—';
    }
    return value.toLocaleString('pt-BR');
  }

  statusBadgeClass(status: GoalStatus): string {
    switch (status) {
      case 'CRITICAL':
        return 'is-err';
      case 'ATTENTION':
        return 'is-warn';
      default:
        return 'is-ok';
    }
  }

  statusTextClass(status: GoalStatus): string {
    switch (status) {
      case 'CRITICAL':
        return 'text-danger';
      case 'ATTENTION':
        return 'text-warning';
      default:
        return 'text-success';
    }
  }

  countStatus(value: number, attentionAt: number, criticalAt: number): GoalStatus {
    if (value >= criticalAt) return 'CRITICAL';
    return value >= attentionAt ? 'ATTENTION' : 'OK';
  }

  queueStatus(): GoalStatus {
    const queue = this.live?.queueNow ?? 0;
    const limit = this.live?.queueAlertAt ?? 10;
    if (queue > limit * 2) return 'CRITICAL';
    return queue > limit ? 'ATTENTION' : 'OK';
  }
}
